import { Route, Switch, withRouter } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { useInjectSaga } from "./sagas/injectSaga";

import routeConstant from "common/routeConstants";
import profileSaga from "./sagas/profileSaga";
import projectSaga from "./sagas/projectSaga";

import Loader from "components/Loader";
import PrivateRoute from "HOC/PrivateRoute";
import PublicRoute from "HOC/PublicRoute";
import Loadable from "react-loadable";
import notificationsSaga from "sagas/notificationsSaga";

const loadable = (loader) =>
  Loadable({
    loader,
    delay: false,
    loading: () => <Loader />,
  });

const Login = loadable(
  () => import(/* webpackChunkName: 'Login' */ "containers/Login"),
);

const Signup = loadable(
  () => import(/* webpackChunkName: 'Signup' */ "containers/Login"),
);

const ForgotPassword = loadable(
  () =>
    import(
      /* webpackChunkName: 'ForgotPassword' */ "containers/ForgotPassword"
    ),
);

const ResetPassword = loadable(
  () =>
    import(/* webpackChunkName: 'ResetPassword' */ "containers/ResetPassword"),
);

const ResetPasswordConfirm = loadable(
  () =>
    import(
      /* webpackChunkName: 'ResetPasswordConfirm' */ "containers/ResetPasswordConfirm"
    ),
);

const PublicProjectView = loadable(
  () =>
    import(/* webpackChunkName: 'PublicProjectView' */ "containers/PublicView"),
);

const Profile = loadable(
  () => import(/* webpackChunkName: 'Profile' */ "containers/Profile"),
);

const UsersList = loadable(
  () => import(/* webpackChunkName: 'Profile' */ "containers/UsersList"),
);

const ChangePassword = loadable(
  () =>
    import(
      /* webpackChunkName: 'ChangePassword' */ "containers/ChangePassword"
    ),
);

const ChangePasswordConfirm = loadable(
  () =>
    import(
      /* webpackChunkName: 'ChangePasswordConfirm' */ "containers/ChangePasswordConfirm"
    ),
);

const CreateProject = loadable(
  () =>
    import(/* webpackChunkName: 'CreateProject' */ "containers/CreateProject"),
);

const Project = loadable(
  () => import(/* webpackChunkName: 'Project' */ "containers/Project"),
);

const NoProjects = loadable(
  () => import(/* webpackChunkName: 'NoProjects' */ "containers/NoProjects"),
);

const HomeMobile = loadable(
  () => import(/* webpackChunkName: 'HomeMobile' */ "containers/HomeMobile"),
);
const Milestone = loadable(
  () => import(/* webpackChunkName: 'Milestone' */ "containers/Milestone"),
);
const ArchivedProjects = loadable(
  () =>
    import(
      /* webpackChunkName: 'ArchivedProjects' */ "containers/ArchivedProjects"
    ),
);
const Notifications = loadable(
  () =>
    import(/* webpackChunkName: 'Notifications' */ "containers/Notifications"),
);
const SignupConfirm = loadable(
  () =>
    import(/* webpackChunkName: 'SignupConfirm' */ "containers/SignupConfirm"),
);
const EmailVerification = loadable(
  () =>
    import(
      /* webpackChunkName: 'EmailVerification' */ "containers/EmailVerification"
    ),
);
const ProjectSettings = loadable(
  () =>
    import(
      /* webpackChunkName: 'ProjectSettings' */ "containers/ProjectSettings"
    ),
);
const ProjectList = loadable(
  () => import(/* webpackChunkName: 'ProjectList' */ "containers/ProjectList"),
);
const TnCView = loadable(
  () => import(/* webpackChunkName: 'PageNotFound' */ "containers/TnC"),
);
const PrivacyPolicyView = loadable(
  () =>
    import(/* webpackChunkName: 'PrivacyPolicy' */ "containers/PrivacyPolicy"),
);
const Forbidden = loadable(
  () => import(/* webpackChunkName: 'PageNotFound' */ "containers/Forbidden"),
);
const PageNotFound = loadable(
  () =>
    import(/* webpackChunkName: 'PageNotFound' */ "containers/PageNotFound"),
);

const Routes = (props) => {
  const { location } = props;
  useInjectSaga({ key: "projectSaga", saga: projectSaga });
  useInjectSaga({ key: "profileSaga", saga: profileSaga });
  useInjectSaga({ key: "notificationsSaga", saga: notificationsSaga });
  return (
    <div className="Wrapper">
      <TransitionGroup className="transition-group">
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={true}
          newestOnTop={true}
          closeOnClick
          closeButton={true}
          theme="dark"
        />
        <CSSTransition
          key={location.key}
          timeout={{ enter: 300, exit: 300 }}
          classNames={"fade"}
        >
          <section className="route-section">
            <Switch location={location}>
              <PublicRoute
                exact
                path={routeConstant.login}
                component={Login}
                {...props}
              />
              <PublicRoute
                exact
                path={routeConstant.signup}
                component={Signup}
                {...props}
              />
              <PublicRoute
                exact
                path={routeConstant.signupConfirm}
                component={SignupConfirm}
                {...props}
              />
              <PublicRoute
                exact
                path={routeConstant.forgotPassword}
                component={ForgotPassword}
                {...props}
              />
              <PublicRoute
                exact
                path={routeConstant.resetPassword}
                component={ResetPassword}
                {...props}
              />
              <PublicRoute
                exact
                path={routeConstant.resetPasswordConfirm}
                component={ResetPasswordConfirm}
                {...props}
              />
              <Route
                exact
                path={"/public/project/:projectId/:publicId"}
                component={PublicProjectView}
                {...props}
              />
              <Route
                exact
                path={routeConstant.emailVerification}
                component={EmailVerification}
                {...props}
              />
              <PrivateRoute
                exact
                path={routeConstant.profile}
                component={Profile}
                {...props}
              />
              <PrivateRoute
                exact
                path={routeConstant.changePassword}
                component={ChangePassword}
                {...props}
              />
              <PrivateRoute
                exact
                path={routeConstant.changePasswordConfirm}
                component={ChangePasswordConfirm}
                {...props}
              />
              <PrivateRoute
                exact
                path={routeConstant.createProject}
                component={CreateProject}
                {...props}
                isAdminRoute
              />
              <PrivateRoute
                exact
                path={routeConstant.projectSettings}
                component={ProjectSettings}
                {...props}
                isAdminRoute
              />
              <PrivateRoute
                exact
                path={routeConstant.project}
                component={Project}
                {...props}
              />
              <PrivateRoute
                exact
                path={routeConstant.noProjects}
                component={NoProjects}
                {...props}
              />
              <PrivateRoute
                exact
                path={routeConstant.homeMobile}
                component={HomeMobile}
                {...props}
              />
              <PrivateRoute
                exact
                component={Milestone}
                path={routeConstant.milestoneView}
                {...props}
              />
              <PrivateRoute
                exact
                path={routeConstant.archivedProjects}
                component={ArchivedProjects}
                {...props}
              />
              <PrivateRoute
                exact
                path={routeConstant.notifications}
                component={Notifications}
                {...props}
              />
              <PrivateRoute
                exact
                path={routeConstant.milestoneCreateRoute}
                component={Milestone}
                {...props}
              />
              <PrivateRoute
                exact
                path={routeConstant.projectList}
                component={ProjectList}
                {...props}
              />
              <PrivateRoute
                exact
                path={routeConstant.users}
                component={UsersList}
                {...props}
                isAdminRoute
              />
              <Route
                exact
                path={routeConstant.tnc}
                component={TnCView}
                {...props}
              />
              <Route
                exact
                path={routeConstant.privacy}
                component={PrivacyPolicyView}
                {...props}
              />
              <Route
                exact
                path={routeConstant.forbidden}
                component={Forbidden}
                {...props}
              />
              <Route exact path="*" component={PageNotFound} />
            </Switch>
          </section>
        </CSSTransition>
      </TransitionGroup>
    </div>
  );
};

export default withRouter(Routes);
